import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FAQ as FAQ_DATA } from '../../utils/constants';
import { fadeUp, spring, stagger, viewportOnce } from '../../utils/animations';

function FAQItem({ item, isOpen, onToggle }: {
  item: typeof FAQ_DATA[0];
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <motion.div
      className="border-b border-white/[0.05] last:border-0"
      variants={fadeUp}
      transition={spring}
    >
      <button
        className="w-full py-6 flex items-center justify-between text-left group"
        onClick={onToggle}
      >
        <span className="text-[15px] sm:text-base font-medium text-white/80 group-hover:text-white transition-colors duration-200 pr-8">
          {item.q}
        </span>
        <motion.span
          className="text-white/30 flex-shrink-0 text-lg select-none"
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        >
          +
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
              opacity: { duration: 0.2, delay: isOpen ? 0.1 : 0 },
            }}
            className="overflow-hidden"
          >
            <p className="text-white/45 leading-relaxed pb-6 pr-12 text-sm">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [ref, inView] = useInView(viewportOnce);

  return (
    <section className="section-padding relative overflow-hidden">
      <div className="container-custom relative z-10" ref={ref}>
        {/* Header */}
        <motion.h2
          className="text-4xl sm:text-5xl font-bold text-center text-white mb-16"
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          transition={spring}
        >
          Questions
        </motion.h2>

        {/* FAQ items — staggered */}
        <motion.div
          className="max-w-2xl mx-auto glass-card px-8"
          variants={stagger(0.08, 0.2)}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {FAQ_DATA.map((item, i) => (
            <FAQItem
              key={i}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
